import React, { Suspense, lazy, useEffect, useState } from 'react'
import { useAppDispatch } from '../../../store'
import { type ApplyFiltersAndDispatchParams } from '../../../components/CustomTable/types'
import PAGE_TITLES from '../../../consts/titles'
import LoadingPageV2 from '../../../pages/LoadingPageV2'
import { useParams } from 'react-router-dom'
import { getTermsSectionsApi } from '../../../store/slices/termsSectionsSlice/api'
import { getTermsSectionsList, loading } from '../../../store/slices/termsSectionsSlice/selectors'
import { CreateAndEditTermsSection } from './CreateAndEditTermsSection'
import DeleteSection from './DeleteSection'
import { Box, Typography } from '@mui/material'
import TermsTopbarActions from './TermsTopbarActions'
import { cdgCellCommonStyles } from '../../../components/CustomTable/generateCells'
import { formatDateByUserTimeZone } from '../../../helpers/formatDateByUserTimeZone'
import { type TableCell } from '../../../models/CompletionRate'
import { type ISection } from '../../../models/TermsSection'
import { useAppSearchParams } from '../../../hooks/useAppSearchParams'
import GenericModal from '../../../components/GenericModal'
import { useTaskStatus } from '../../../hooks/useTaskStatus'
import { ExportBackgroundTaskRunningModal } from '../../../components/ExportBackgroundTaskRunningModal'

const AppTable = lazy(async () => await import('../../../components/CustomTable'))

const TermsSectionTable: React.FC = () => {
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<ISection | Record<string, any>>({})
  const { searchParams } = useAppSearchParams()

  const selectedScope = searchParams.get('scope') as 'my' | 'all'
  const { id: urlTermId } = useParams()

  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({ pageSize: 10, termId: urlTermId, scope: selectedScope ?? 'my' })

  const isLoading = loading()
  const termsSectionList = getTermsSectionsList()
  const dispatch = useAppDispatch()
  const headerName = termsSectionList?.header?.headerName || 'Course Sections'
  const queryParams = { ...params, termId: urlTermId }

  const {
    taskId: uploadTaskId,
    setTaskId: setUploadTaskId,
    showBackgroundModal: showUploadBlockedModal,
    setShowBackgroundModal: setShowUploadBlockedModal,
    isCheckingStatus: isUploadRunning
  } = useTaskStatus('uploadTask_termsSections')

  const {
    taskId: exportTaskId,
    setTaskId: setExportTaskId,
    showBackgroundModal: showExportBlockedModal,
    setShowBackgroundModal: setShowExportBlockedModal,
    isCheckingStatus: isExportRunning,
    checkStatusBeforeExport
  } = useTaskStatus('exportTask_termsSections')

  const isAnyTaskRunning = isUploadRunning || isExportRunning

  const openBlockedModal = (): void => {
    if (isUploadRunning) setShowUploadBlockedModal(true)
    else setShowExportBlockedModal(true)
  }

  useEffect(() => {
    if (urlTermId && params && Object.keys(params).length > 0) {
      void dispatch(getTermsSectionsApi({ urlId: urlTermId, params }))
    }
  }, [JSON.stringify(params), urlTermId])

  const allowedActions = termsSectionList?.actions ?? []

  const overrides = {
    startTimestamp: (cell: TableCell) => (
      <Typography sx={cdgCellCommonStyles}>{formatDateByUserTimeZone(cell.startTimestamp as string, 'MM/dd/yyyy')}</Typography>
    ),
    endTimestamp: (cell: TableCell) => (
      <Typography sx={cdgCellCommonStyles}>{formatDateByUserTimeZone(cell.endTimestamp as string, 'MM/dd/yyyy')}</Typography>
    )
  }
  return (
    <Box >
      <Suspense fallback={<LoadingPageV2 title={PAGE_TITLES.TERMS} />}>
        <AppTable
          createAction={<CreateAndEditTermsSection params={queryParams} isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
          customTopbarAction={
            <TermsTopbarActions
              params={queryParams}
              allowedActions={allowedActions}
              setExportTaskId={setExportTaskId}
              setUploadTaskId={setUploadTaskId}
              checkStatusBeforeExport={checkStatusBeforeExport}
              isTaskRunning={isAnyTaskRunning}
              isUploadRunning={isUploadRunning}
              onTaskBlock={openBlockedModal}
            />
          }
          editAction={<CreateAndEditTermsSection params={queryParams} isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
          list={termsSectionList}
          loading={isLoading}
          deleteAction={<DeleteSection isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
          featureName="Courses"
          params={params}
          setParams={setParams}
          showScopeToggle={true}
          scopeToggleTitle={headerName}
          overrides={overrides}
          onRowClick={(row: Record<string, any>) => { setSelectedRow(row?.row?.data) }}
        />
      </Suspense>

      <ExportBackgroundTaskRunningModal
        isOpen={showExportBlockedModal}
        onClose={() => { setShowExportBlockedModal(false) }}
        taskId={exportTaskId ?? ''}
      />
      <ExportBackgroundTaskRunningModal
        isOpen={showUploadBlockedModal}
        onClose={() => { setShowUploadBlockedModal(false) }}
        taskId={uploadTaskId ?? ''}
      />
      {
        // If a section has more than one instructor, clicking on the instructor cell opens this modal
        // to allow viewing or editing all instructors for that section.
        showUpdateModal &&
        <GenericModal
          isOpen={showUpdateModal}
          title={'Create Course Section'}
          onClose={() => { setShowUpdateModal?.(false) }}
          maxWidth='sm'
          action={false}
        >
          <CreateAndEditTermsSection selectedRow={selectedRow} params={params} handleCloseModal={() => { setShowUpdateModal?.(false) }} />
        </GenericModal>
      }
    </Box>
  )
}

export default TermsSectionTable
